import { DS_COLOR } from '@calm/evexia-design-tokens';
import { UsercryptCheckinEntry } from '@calm/evexia-web-sdk';

export const phqMaxScore = 27;
export const gadMaxScore = 21;

export const phqYAxis = [0, 5, 10, 15, 20, 27];
export const gadYAxis = [0, 5, 10, 15, 21];

export enum CheckinTypeKey {
    Gad = 'gad',
    Phq = 'phq',
    SelfHarm = 'self_harm_thoughts',
    Goal = 'goal',
    Topic = 'topic',
}

export enum ScreeningResult {
    Asymptomatic = 'asymptomatic',
    SubClinical = 'subclinical',
    Mild = 'mild',
    Moderate = 'moderate',
    ModerateSevere = 'moderatesevere',
    Severe = 'severe',
    Suicidal = 'suicidal',
}

export const screeningResultCutoffs = {
    [ScreeningResult.Asymptomatic]: 1,
    [ScreeningResult.SubClinical]: 5,
    [ScreeningResult.Mild]: 10,
    [ScreeningResult.Moderate]: 15,
    [ScreeningResult.ModerateSevere]: 20,
};

export const screeningResultPrograms = {
    [ScreeningResult.Asymptomatic]: '_hjMA1pH4x',
    [ScreeningResult.SubClinical]: '_hjMA1pH4x',
    [ScreeningResult.Mild]: 'CjYSTS1Lk4',
    [ScreeningResult.Moderate]: '4NtKfFfbed',
    [ScreeningResult.ModerateSevere]: 'QaOMoAmQk4',
    [ScreeningResult.Severe]: 'QaOMoAmQk4',
    [ScreeningResult.Suicidal]: 'KmTbKN7mn1',
};

export const screeningResultColors = {
    [ScreeningResult.Asymptomatic]: DS_COLOR.GRAPH_MINIMAL,
    [ScreeningResult.SubClinical]: DS_COLOR.GRAPH_MINIMAL,
    [ScreeningResult.Mild]: DS_COLOR.GRAPH_MILD,
    [ScreeningResult.Moderate]: DS_COLOR.GRAPH_MODERATE,
    [ScreeningResult.ModerateSevere]: DS_COLOR.GRAPH_MODERATE_SEVERE,
    [ScreeningResult.Severe]: DS_COLOR.GRAPH_SEVERE,
    [ScreeningResult.Suicidal]: DS_COLOR.GRAPH_SEVERE,
};

export function classifyResult({
    gadEntry,
    phqEntry,
    selfHarmEntry,
    useModerateSevere = false,
    hideSelfHarmClassification = false,
}: {
    gadEntry?: UsercryptCheckinEntry;
    phqEntry?: UsercryptCheckinEntry;
    selfHarmEntry?: UsercryptCheckinEntry;
    useModerateSevere?: boolean;
    hideSelfHarmClassification?: boolean;
}) {
    if (
        !hideSelfHarmClassification &&
        (selfHarmEntry?.checkins.values[0].value ?? 0) > 0
    ) {
        return ScreeningResult.Suicidal;
    } else {
        const highScore = Math.max(
            gadEntry?.checkins.values[0].value ?? 0,
            phqEntry?.checkins.values[0].value ?? 0
        );

        if (
            highScore >=
                screeningResultCutoffs[ScreeningResult.ModerateSevere] ||
            (!useModerateSevere &&
                highScore >= screeningResultCutoffs[ScreeningResult.Moderate])
        ) {
            return ScreeningResult.Severe;
        } else if (
            useModerateSevere &&
            highScore >= screeningResultCutoffs[ScreeningResult.Moderate]
        ) {
            return ScreeningResult.ModerateSevere;
        } else if (highScore >= screeningResultCutoffs[ScreeningResult.Mild]) {
            return ScreeningResult.Moderate;
        } else if (
            highScore >= screeningResultCutoffs[ScreeningResult.SubClinical]
        ) {
            return ScreeningResult.Mild;
        } else if (
            highScore >= screeningResultCutoffs[ScreeningResult.Asymptomatic]
        ) {
            return ScreeningResult.SubClinical;
        } else {
            return ScreeningResult.Asymptomatic;
        }
    }
}

export function sortCheckins(
    a: UsercryptCheckinEntry,
    b: UsercryptCheckinEntry
) {
    return (
        new Date(a.checkinDate ?? '').getTime() -
        new Date(b.checkinDate || '').getTime()
    );
}
